<script setup>
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Button from 'next/button/Button.vue';
import ParameterRow from '../../components/ParameterRow.vue';

const props = defineProps({
  source: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const initialState = {
  name: props.source.name ?? '',
  description: props.source.description ?? '',
  parameters: Object.entries(props.source.parameters ?? {}).map(
    ([key, value], index) => ({
      key: key ?? '',
      value: value ?? '',
      disabled: true,
      index,
    })
  ),
};

const state = reactive({ ...initialState });

const rules = {
  name: { required, minLength: minLength(1) },
  description: {},
  parameters: {
    hasDuplicates: value => {
      const keys = value.map(p => p.key);
      return !keys.some((key, index) => keys.indexOf(key) !== index);
    },
  },
};

const v$ = useVuelidate(rules, state);

const prepareFormData = () => {
  const parameters = {};
  state.parameters.forEach(parameter => {
    if (parameter.key) {
      parameters[parameter.key] = parameter.value;
    }
  });

  return {
    name: state.name,
    description: state.description,
    parameters,
  };
};

const validate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return null;

  return prepareFormData();
};

const handleSubmit = async () => {
  const isFormValid = await validate();
  if (!isFormValid) return;

  emit('submit', prepareFormData());
};

const addParameter = () => {
  const parameter = {
    key: '',
    value: '',
    index: state.parameters.length,
  };
  state.parameters.push(parameter);
};

const removeParameter = index => {
  state.parameters = state.parameters.filter(
    parameter => parameter.index !== index
  );
};

const parameterHasDuplicate = index => {
  const key = state.parameters[index].key;
  const keys = state.parameters.map(p => p.key);
  return keys.filter(k => k === key).length > 1;
};

const onParameterChange = (index, parameter) => {
  state.parameters = state.parameters.map((parameterBefore, i) => {
    if (index !== i) return parameterBefore;
    return { ...parameterBefore, ...parameter };
  });
};

defineExpose({ validate });
</script>

<template>
  <form class="flex flex-col gap-4 pb-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="
        t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.FORM.NAME.LABEL'
        )
      "
      :message-type="v$.name.$error ? 'error' : 'info'"
      :message="
        v$.name.$error
          ? t(
              'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.FORM.NAME.ERROR'
            )
          : ''
      "
    />

    <TextArea
      v-model="state.description"
      :label="
        t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.FORM.DESCRIPTION.LABEL'
        )
      "
      :message-type="v$.description.$error ? 'error' : 'info'"
      :message="
        v$.description.$error
          ? t(
              'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.FORM.DESCRIPTION.ERROR'
            )
          : ''
      "
    />

    <div class="w-full mt-4">
      <label>
        <span>{{
          $t(
            'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.FORM.PARAMETERS.LABEL'
          )
        }}</span>
      </label>
    </div>
    <ParameterRow
      v-for="(parameter, index) in state.parameters"
      :key="parameter.index"
      :value="parameter"
      :has-duplicate="parameterHasDuplicate(index)"
      :show-label="index === 0"
      :disabled="parameter.disabled"
      @remove="removeParameter(parameter.index)"
      @change="e => onParameterChange(index, e)"
    />

    <div class="mt-2 -ml-1">
      <Button
        :label="
          $t(
            'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.ADD_PARAMETER_BTN_TXT'
          )
        "
        size="xs"
        variant="link"
        icon="i-lucide-plus"
        @click.prevent="addParameter"
      />
    </div>
  </form>
</template>
